import { useState, useEffect } from 'react';
import { Head, Link } from '@inertiajs/react';
import { AdventureAttempt } from '@/types/adventures';
import { FlashMessage } from '@/components/ui';

/**
 * Pagination link from Laravel.
 */
interface PaginationLink {
    url: string | null;
    label: string;
    active: boolean;
}

/**
 * Paginated attempts response.
 */
interface PaginatedAttempts {
    data: AdventureAttempt[];
    links: PaginationLink[];
    last_page: number;
}

/**
 * Props for the Attempts page.
 */
interface Props {
    attempts: PaginatedAttempts;
    inProgressCount: number;
    readyToCompleteCount: number;
}

/**
 * User Adventure Attempts page - displays all user's adventure attempts.
 */
export default function Attempts({ attempts, inProgressCount, readyToCompleteCount }: Props) {
    const [activeTab, setActiveTab] = useState<'all' | 'in_progress' | 'completed'>('all');

    // Filter attempts based on active tab
    const filteredAttempts = attempts.data.filter((attempt) => {
        if (activeTab === 'in_progress') {
            return attempt.status.value === 'in_progress';
        }
        if (activeTab === 'completed') {
            return attempt.status.value !== 'in_progress' && attempt.status.value !== 'pending';
        }
        return true;
    });

    return (
        <>
            <Head title="My Adventures" />

            {/* Flash messages for success/error notifications */}
            <FlashMessage />

            <div className="min-h-screen bg-slate-900 py-8 px-4">
                <div className="max-w-4xl mx-auto">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h1 className="text-2xl font-bold text-white">My Adventures</h1>
                            <p className="text-gray-400 text-sm mt-1">
                                Track your characters' progress
                            </p>
                        </div>
                        <div className="flex items-center gap-4">
                            <Link
                                href="/adventures"
                                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors"
                            >
                                + Start New Adventure
                            </Link>
                            <Link
                                href="/map"
                                className="text-gray-400 hover:text-white transition-colors"
                            >
                                ← Back to Map
                            </Link>
                        </div>
                    </div>

                    {/* Stats Bar */}
                    <div className="grid grid-cols-3 gap-4 mb-6">
                        <div className="bg-slate-800 border border-slate-700 rounded-lg p-4 text-center">
                            <div className="text-2xl font-bold text-white">{attempts.data.length}</div>
                            <div className="text-sm text-gray-400">Total Attempts</div>
                        </div>
                        <div className="bg-slate-800 border border-slate-700 rounded-lg p-4 text-center">
                            <div className="text-2xl font-bold text-blue-400">{inProgressCount}</div>
                            <div className="text-sm text-gray-400">In Progress</div>
                        </div>
                        <div className="bg-slate-800 border border-slate-700 rounded-lg p-4 text-center">
                            <div className="text-2xl font-bold text-green-400">{readyToCompleteCount}</div>
                            <div className="text-sm text-gray-400">Ready to Complete</div>
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="flex gap-2 mb-6">
                        <TabButton
                            active={activeTab === 'all'}
                            onClick={() => setActiveTab('all')}
                            label="All"
                        />
                        <TabButton
                            active={activeTab === 'in_progress'}
                            onClick={() => setActiveTab('in_progress')}
                            label={`In Progress (${inProgressCount})`}
                        />
                        <TabButton
                            active={activeTab === 'completed'}
                            onClick={() => setActiveTab('completed')}
                            label="Completed"
                        />
                    </div>

                    {/* Attempts List */}
                    {filteredAttempts.length === 0 ? (
                        <EmptyState activeTab={activeTab} />
                    ) : (
                        <div className="space-y-4">
                            {filteredAttempts.map((attempt) => (
                                <AttemptCard key={attempt.id} attempt={attempt} />
                            ))}
                        </div>
                    )}

                    {/* Pagination */}
                    {attempts.last_page > 1 && (
                        <Pagination links={attempts.links} />
                    )}
                </div>
            </div>
        </>
    );
}

/**
 * TabButton - Tab navigation button.
 */
function TabButton({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
    return (
        <button
            onClick={onClick}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                active
                    ? 'bg-purple-600 text-white'
                    : 'bg-slate-800 text-gray-400 hover:bg-slate-700 hover:text-white'
            }`}
        >
            {label}
        </button>
    );
}

/**
 * EmptyState - Displayed when no attempts match the filter.
 */
function EmptyState({ activeTab }: { activeTab: string }) {
    const messages: Record<string, { title: string; subtitle: string }> = {
        all: {
            title: "No adventures yet",
            subtitle: "Start your first adventure to begin earning rewards!",
        },
        in_progress: {
            title: "No adventures in progress",
            subtitle: "All your characters are available for new adventures.",
        },
        completed: {
            title: "No completed adventures",
            subtitle: "Complete an adventure to see your results here.",
        },
    };

    const message = messages[activeTab] || messages.all;

    return (
        <div className="text-center py-12 bg-slate-800 border border-slate-700 rounded-lg">
            <p className="text-lg text-gray-300">{message.title}</p>
            <p className="text-sm text-gray-500 mt-2">{message.subtitle}</p>
            <Link
                href="/adventures"
                className="inline-block mt-4 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors"
            >
                Browse Adventures
            </Link>
        </div>
    );
}

/**
 * AttemptCard - Displays a single adventure attempt.
 */
function AttemptCard({ attempt }: { attempt: AdventureAttempt }) {
    const isInProgress = attempt.status.value === 'in_progress';

    // Get the hex ID from the first character in the attempt
    // All characters in an attempt belong to the same user's hex
    const hexId = attempt.characters[0]?.hex_id;

    // Build the URL - use hex layout if we have a hex ID, otherwise fallback to standalone route
    const href = hexId
        ? `/hex/${hexId}?tab=attempt-detail&attemptId=${attempt.id}`
        : `/adventures/attempts/${attempt.id}`;

    return (
        <Link
            href={href}
            className="block bg-slate-800 border border-slate-700 rounded-lg p-5 hover:border-purple-500 transition-colors"
        >
            <div className="flex items-start justify-between">
                {/* Adventure Info */}
                <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-white">
                            {attempt.adventure?.name || 'Unknown Adventure'}
                        </h3>
                        <span className={`px-2 py-0.5 text-xs rounded ${attempt.status.tailwindClass}`}>
                            {attempt.status.icon} {attempt.status.label}
                        </span>
                    </div>

                    {/* Characters */}
                    <div className="flex items-center gap-2 text-sm text-gray-400 mb-2">
                        <span>👥</span>
                        <span>
                            {attempt.characters.map((c) => c.name).join(', ') || 'No characters'}
                        </span>
                    </div>

                    {/* Outcome for completed attempts */}
                    {attempt.outcome && (
                        <div className="flex items-center gap-2 text-sm">
                            <span className={attempt.outcome.tailwindClass}>
                                {attempt.outcome.icon} {attempt.outcome.label}
                            </span>
                            {attempt.roll_result !== null && (
                                <span className="text-gray-500">
                                    (Rolled {attempt.roll_result} vs {attempt.success_chance}%)
                                </span>
                            )}
                        </div>
                    )}
                </div>

                {/* Timer or Action */}
                <div className="text-right">
                    {isInProgress ? (
                        <CountdownTimer
                            remainingSeconds={attempt.remaining_seconds}
                            isReady={attempt.is_ready_to_complete}
                        />
                    ) : (
                        <div className="text-sm text-gray-500">
                            {attempt.completed_at && formatDate(attempt.completed_at)}
                        </div>
                    )}
                </div>
            </div>
        </Link>
    );
}

/**
 * CountdownTimer - Displays remaining time or "Ready" status.
 */
function CountdownTimer({ remainingSeconds, isReady }: { remainingSeconds: number; isReady: boolean }) {
    const [seconds, setSeconds] = useState(remainingSeconds);

    // Countdown effect
    useEffect(() => {
        if (seconds <= 0) return;

        const timer = setInterval(() => {
            setSeconds((prev) => Math.max(0, prev - 1));
        }, 1000);

        return () => clearInterval(timer);
    }, [seconds]);

    if (isReady || seconds <= 0) {
        return (
            <div className="px-3 py-1 bg-green-600 text-white rounded-lg text-sm font-medium animate-pulse">
                ✓ Ready to Complete
            </div>
        );
    }

    // Format time
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    const timeString = hours > 0
        ? `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
        : `${minutes}:${secs.toString().padStart(2, '0')}`;

    return (
        <div className="text-right">
            <div className="text-lg font-mono text-blue-400">{timeString}</div>
            <div className="text-xs text-gray-500">remaining</div>
        </div>
    );
}

/**
 * Format ISO date string to readable format.
 */
function formatDate(isoString: string): string {
    const date = new Date(isoString);
    return date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

/**
 * Pagination - Displays pagination links.
 */
function Pagination({ links }: { links: PaginationLink[] }) {
    return (
        <div className="mt-6 flex justify-center gap-1">
            {links.map((link, index) => (
                <a
                    key={index}
                    href={link.url || '#'}
                    className={`px-3 py-1 rounded text-sm ${
                        link.active
                            ? 'bg-purple-600 text-white'
                            : link.url
                            ? 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                            : 'bg-slate-800 text-gray-500 cursor-not-allowed'
                    }`}
                    dangerouslySetInnerHTML={{ __html: link.label }}
                />
            ))}
        </div>
    );
}

